// Lifting & Plant — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const LIFTING_PLANT: Record<string, TemplateSpecific> = {
  "lift-crane": {
    whoFor:
      "This RAMS is for lifting contractors and slinger/signallers carrying out mobile crane lifts where a principal contractor or client has asked for a documented lift plan before the crane comes on site. If you are running a crane lift on a construction site, the PC will expect a BS 7121 lift plan signed by an appointed person, and a generic method statement will not be accepted. It gives you the recognised hazards, the lift-categorisation framework and the controls a reviewer expects to see.",
    scope:
      "Covers mobile crane lifting operations on a construction site, including basic and intermediate lifts planned in advance by an appointed person. It applies to the rigging, lifting, slewing and landing of loads within the crane's rated capacity on prepared, assessed ground. Tandem (multi-crane) lifts and complex lifts requiring a dedicated written lift plan are excluded and must be planned separately. Contract lifts, where the crane hire company plans and supervises the lift under their own appointed person, are a different arrangement and are noted but not covered by this document.",
    legislation: [
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER)",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "BS 7121 — Code of practice for safe use of cranes",
      "The Construction (Design and Management) Regulations 2015",
      "Health and Safety at Work etc. Act 1974",
    ],
    sequence: [
      "The appointed person plans the lift, categorises it as basic, intermediate or complex, selects the crane and tackle, and produces the lift plan.",
      "Ground conditions and proximity hazards (overhead lines, excavations, structures) are assessed and the crane position, outrigger spread and mat/packing requirements confirmed.",
      "On the day, the crane is set up level on outriggers with the calculated packing, and exclusion zones for the slew radius and load path are established.",
      "A pre-lift check and briefing covers the load weight, the rated capacity at the working radius, the agreed signals and the responsibilities of the slinger/signaller and operator.",
      "The load is rigged, the weight verified against the lift plan, and a trial lift of a few inches confirms stability and sling balance before continuing.",
      "The load is lifted, slewed and landed under the direction of the signaller, keeping personnel clear of the load and the slew zone throughout.",
      "Weather limits (wind speed against the load's sail area, visibility) are monitored and the lift is suspended if conditions exceed the crane's stated limits.",
    ],
    hazards: [
      {
        hazard: "Crane overturn from incorrect ground assessment or overloading at radius",
        who: ["Operator", "Slinger/signaller", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Appointed person calculates ground bearing pressure and specifies crane mats/packing under outriggers",
          "Load weight verified and checked against the rated capacity for the actual working radius",
          "Crane set up and confirmed level on fully extended outriggers before any lift",
          "Lift stopped if the load cannot be kept within the rated capacity at all radii in the path",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Load fall from failure of slings, shackles or lifting accessories",
        who: ["Slinger/signaller", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Lifting accessories within current LOLER thorough examination (6-monthly) and within SWL",
          "Slinger inspects tackle before use and rejects any damaged or unmarked accessory",
          "Trial lift to confirm sling balance and load integrity before full lift",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Contact with overhead power lines during slewing",
        who: ["Operator", "Slinger/signaller", "Site personnel"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Overhead lines identified at planning stage and exclusion distances set per GS6",
          "Goal posts, barriers and slew limits established to keep the boom outside the safe clearance",
          "Lift repositioned or lines diverted/isolated where safe clearance cannot be maintained",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Person struck by load or trapped in the slew radius",
        who: ["Slinger/signaller", "Site personnel"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Exclusion zone barriered around the full slew radius and load path",
          "Only the slinger/signaller approaches the load; the area is kept clear of all others",
          "Load never carried over occupied areas; tag lines used to control the load",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Loss of control in high wind",
        who: ["Operator", "Slinger/signaller"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Maximum wind speed for the load's sail area taken from the lift plan/crane chart",
          "Anemometer reading taken before and monitored during the lift",
          "Lift suspended and the load landed if wind approaches the stated limit",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Hi-vis clothing",
      "Safety helmet",
      "Safety boots (steel toe and midsole)",
      "Gloves for handling tackle",
    ],
    competence: [
      "Appointed person competent to plan lifts under BS 7121",
      "CPCS/NPORS qualified mobile crane operator for the crane type",
      "Trained and competent slinger/signaller",
      "Crane supervisor for intermediate lifts as required by the lift plan",
    ],
    permits: [
      "Lift plan signed off by the appointed person before lifting begins",
      "Permit to dig where outrigger loads act near buried services or excavations",
    ],
    equipment: [
      "Mobile crane of suitable capacity and reach",
      "Crane outrigger mats and timber packing",
      "Slings, chains and shackles within LOLER examination",
      "Tag lines for load control",
      "Anemometer",
      "Exclusion-zone barriers and signage",
    ],
    pcChecks: [
      "The lift plan is signed by a named appointed person and categorises the lift as basic, intermediate or complex — a generic 'lifts to be planned on site' line gets the RAMS returned.",
      "Ground bearing pressure and outrigger packing are addressed, not just assumed adequate.",
      "Current LOLER thorough examination reports for the crane and accessories are referenced and available.",
    ],
    faqs: [
      {
        q: "Do I need an appointed person for every crane lift?",
        a: "Yes — LOLER requires every lifting operation to be properly planned by a competent person, and on a construction site that planning role is the appointed person under BS 7121. The appointed person decides the lift category, selects the crane and tackle, and signs off the lift plan. For a basic, routine lift the planning can be straightforward, but the appointed-person responsibility still applies. You cannot delegate it to the crane operator simply because the lift looks simple.",
      },
      {
        q: "What is the difference between a basic, intermediate and complex lift?",
        a: "BS 7121 categorises lifts by their difficulty and consequences. A basic lift is a routine, low-risk lift that a competent operator and slinger can handle within a standard plan. An intermediate lift needs more detailed planning and usually a crane supervisor on site to control it. A complex lift — tandem lifts, lifts over occupied areas, or non-standard loads — needs a dedicated written lift plan and is outside the scope of this template.",
      },
    ],
  },

  "lift-telehandler": {
    whoFor:
      "Written for groundworkers and telehandler operators moving materials and placing loads on site, where the principal contractor wants a task-specific risk assessment rather than a generic plant document. Telehandlers are stable when the load is low and retracted but lose stability fast as the boom extends, and that is where most incidents happen. This RAMS sets out the load-chart discipline and travel controls a reviewer expects from a competent telehandler operation.",
    scope:
      "Covers the operation of a rough-terrain telehandler for lifting, carrying and placing palletised and loose materials on a construction site. It includes travelling with the load lowered and retracted, and placing loads at height within the machine's rated load chart. Lifting freely suspended loads using LOLER lifting accessories is included only where the operator holds the suspended-loads category and the correct attachments are fitted. The document does not cover use as a mobile elevating work platform or any attachment the machine is not rated or certified for.",
    legislation: [
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER)",
      "BS 7121 — Code of practice for safe use of cranes (Part 4, loader cranes and telehandlers)",
      "The Construction (Design and Management) Regulations 2015",
      "Workplace (Health, Safety and Welfare) Regulations 1992",
    ],
    sequence: [
      "Operator carries out pre-use checks: tyres, forks/attachment locking, hydraulics, brakes, mirrors, audible reversing alarm and the load chart in the cab.",
      "The load weight and lift height are confirmed and checked against the machine's load chart for that boom extension before lifting.",
      "The load is picked up with forks fully under and the load tilted back, then lowered and the boom retracted before any travel.",
      "The machine travels with the load low and retracted, at walking pace, on assessed routes clear of pedestrians and within ground limits.",
      "At the placing position the machine is brought to a stop, levelled, and the load raised and extended only when stationary.",
      "The load is placed under control, the forks withdrawn cleanly, and the boom lowered and retracted before moving off.",
    ],
    hazards: [
      {
        hazard: "Forward/sideways overturn from extending the boom beyond the load chart",
        who: ["Operator", "Banksman", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Load weight and centre of gravity confirmed and checked against the load chart before lifting",
          "Longitudinal stability indicator/load moment device working and observed by the operator",
          "Machine kept level and stationary while the boom is extended or the load is at height",
          "Lift refused if the load chart shows it is outside the safe envelope at that reach",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Overturn while travelling with the load raised",
        who: ["Operator", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Load always lowered and the boom retracted before the machine travels",
          "Travel at walking pace on firm, assessed ground away from slopes and edges",
          "Seat belt worn so the operator stays within the protective cab structure",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Dropped suspended load from incorrect or uncertified lifting accessories",
        who: ["Slinger", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Suspended loads lifted only with a LOLER-examined lifting hook/jib certified for the machine",
          "Operator holds the suspended-loads category and a trained slinger directs the lift",
          "Lifting accessories within 6-monthly thorough examination and inspected before use",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Pedestrian struck by the machine or its load",
        who: ["Banksman", "Site personnel"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Segregated pedestrian routes and a banksman where vision is restricted",
          "Reversing alarm and flashing beacon working; reversing minimised by route planning",
          "Operator stops if anyone enters the working area until they are clear",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Load instability from poorly stacked or unsecured materials on forks",
        who: ["Operator", "Site personnel"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Loads checked for stability and banding before lifting; loose materials carried in a cage",
          "Forks set to the correct width and fully under the load",
          "Load tilted back and kept low while moving",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Hi-vis clothing",
      "Safety helmet",
      "Safety boots (steel toe)",
      "Gloves when slinging or handling loads",
    ],
    competence: [
      "CPCS/NPORS qualified telehandler operator for the machine type",
      "Suspended-loads endorsement where a jib/hook is used for free-suspended lifts",
      "Trained banksman/slinger where loads are slung or vision is restricted",
    ],
    permits: [
      "Authorisation to operate issued by the site manager for the named operator",
      "Lift plan where the telehandler is used to lift freely suspended loads",
    ],
    equipment: [
      "Rough-terrain telehandler with current load chart in the cab",
      "Pallet forks and any rated attachment (bucket, jib, hook)",
      "LOLER-examined lifting jib/hook for suspended loads",
      "Material cage for loose loads",
      "Banksman radios/whistle",
      "Reversing beacon and audible alarm",
    ],
    pcChecks: [
      "The RAMS references the load chart and ties lifts to boom extension/reach — a flat 'lift within capacity' statement without the chart gets queried.",
      "Travel is specified with the load lowered and retracted, not carried raised between positions.",
      "If suspended loads are mentioned, the operator's suspended-loads category and a LOLER-examined accessory are named.",
    ],
    faqs: [
      {
        q: "Can a telehandler lift a freely suspended load?",
        a: "Only if it is set up and rated for it. The machine must have a certified lifting attachment such as a hook or jib, that attachment and the slings must be within LOLER thorough examination, and the operator must hold the suspended-loads category. Lifting a swinging load changes the machine's stability and brings the lift under LOLER as a planned lifting operation, so it needs a lift plan and usually a slinger. Hanging a load off the forks with a chain is not acceptable.",
      },
      {
        q: "Why must the boom be retracted before travelling?",
        a: "A telehandler's stability depends heavily on keeping the load close to the machine and low to the ground. With the boom extended the load's weight acts well forward of the front axle, and any slope, pothole or sharp turn can tip the machine forward or sideways. Lowering and retracting before moving keeps the centre of gravity inside the wheelbase. This is the single most important habit for preventing telehandler overturns.",
      },
    ],
  },

  "lift-excavator": {
    whoFor:
      "This RAMS is for plant operators and groundworkers running a 360-degree excavator on earthworks, drainage and lifting duties, on sites where the principal contractor expects the quick-hitch and slew-radius risks dealt with explicitly. Quick-hitch failures and people struck in the slew zone are recurring causes of excavator fatalities, so a competent RAMS has to name those controls. It covers the machine in both digging and lifting modes and the checks a reviewer looks for before signing it off.",
    scope:
      "Covers the operation of a tracked or wheeled 360-degree excavator for excavation, grading and lifting on a construction site. It includes the use of a quick-hitch coupler to change buckets and attachments, and lifting operations carried out with the excavator under LOLER. It applies to work within the excavator's rated lift capacity using a certified lifting point. It does not cover demolition with specialist attachments, work near unsupported excavations beyond the assessed batter, or use as a crane for loads outside the machine's rated lifting chart.",
    legislation: [
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER)",
      "The Construction (Design and Management) Regulations 2015",
      "Health and Safety at Work etc. Act 1974",
      "HSE Safety Alert — Hydraulic excavator quick-hitch attachments",
    ],
    sequence: [
      "Operator carries out pre-start checks including tracks/tyres, hydraulics, the quick-hitch coupler and its safety pin, the lifting point and the certified rated capacity indicator.",
      "Each attachment change is made by fully engaging the quick-hitch and confirming the safety pin/locking device is correctly fitted before the bucket is used.",
      "The dig is set out, underground services located, and the excavation battered or supported to the assessed angle to prevent collapse.",
      "An exclusion zone is barriered around the full slew radius of the machine and kept clear of pedestrians throughout.",
      "Excavation proceeds with the operator working to the dig sequence, keeping the bucket clear of the cab arc and not over-reaching.",
      "Where the excavator lifts pipes or other loads, the certified lifting point, rated capacity and a trained slinger are used and the lift kept within the machine's chart.",
    ],
    hazards: [
      {
        hazard: "Bucket or attachment dislodged by quick-hitch failure",
        who: ["Operator", "Slinger", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Use a fully automatic or correctly pinned semi-automatic quick-hitch per the HSE safety alert",
          "Operator visually confirms the locking pin/device is engaged after every attachment change",
          "Daily check of the coupler and locking mechanism recorded as part of pre-start checks",
          "Never crowd the bucket back towards the cab to seat an attachment",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Person struck or crushed in the slew radius / counterweight zone",
        who: ["Slinger", "Site personnel", "Banksman"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Full slew radius barriered as an exclusion zone before work starts",
          "Banksman controls anyone who must approach; operator stops the slew if a person enters",
          "Machine positioned so the counterweight cannot trap people against structures",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Excavation collapse onto operatives in the trench",
        who: ["Groundworker", "Site personnel"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Excavation battered back or supported to the assessed angle of repose",
          "Spoil and the machine kept back from the trench edge by the set distance",
          "No entry into an unsupported trench; trench support installed before access",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Dropped load when lifting with the excavator",
        who: ["Slinger", "Site personnel"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Lift only from a certified lifting point with a rated capacity indicator/check valves fitted",
          "Load kept within the excavator's lifting chart for the radius and a trained slinger used",
          "Lifting accessories within LOLER thorough examination and inspected before use",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Contact with buried or overhead services",
        who: ["Operator", "Groundworker"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Services located with drawings and a CAT scan before digging; trial holes hand-dug",
          "Safe digging practice near located services; overhead lines guarded with goal posts",
          "Work stopped and re-assessed if an unrecorded service is found",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
    ],
    ppe: [
      "Hi-vis clothing",
      "Safety helmet",
      "Safety boots (steel toe and midsole)",
      "Gloves when slinging or handling pipes",
    ],
    competence: [
      "CPCS/NPORS qualified 360-degree excavator operator for the machine size",
      "Operator trained in the specific quick-hitch type fitted",
      "Trained and competent slinger/banksman for lifting and exclusion control",
    ],
    permits: [
      "Permit to dig confirming services are located and the excavation is planned",
      "Lift plan where the excavator is used as a crane for pipes or other loads",
    ],
    equipment: [
      "360-degree tracked or wheeled excavator with rated capacity indicator",
      "Quick-hitch coupler with locking pin/device",
      "Buckets and attachments certified for the machine",
      "Certified lifting eye and LOLER-examined slings for pipe lifts",
      "CAT and Genny for service detection",
      "Exclusion-zone barriers and goal posts",
    ],
    pcChecks: [
      "The quick-hitch type is named and the RAMS states the locking pin/device is confirmed after every attachment change — a silent quick-hitch is the first thing a competent reviewer queries.",
      "An exclusion zone covering the full slew radius is specified, not just 'keep clear of the machine'.",
      "If the excavator lifts, the certified lifting point, rated capacity indicator and LOLER examination are referenced.",
    ],
    faqs: [
      {
        q: "Why are quick-hitches such a high-risk item on excavators?",
        a: "Semi-automatic quick-hitches rely on the operator fitting a safety pin or locking device after each attachment change, and missed or wrong-sized pins have caused buckets to fall off and kill people working nearby. The HSE issued safety alerts specifically about this, and it is one of the recognised causes of excavator fatalities. A competent RAMS must state which hitch type is fitted, that the locking device is checked after every change, and that the coupler is inspected daily. Fully automatic hitches reduce the reliance on the operator but still need checking.",
      },
      {
        q: "Can I use an excavator to lift pipes and other loads?",
        a: "Yes, but only as a planned lifting operation under LOLER. The machine must have a certified lifting point, a rated capacity indicator and hose-burst check valves, and the load must stay within the lifting chart for the working radius. You also need a trained slinger to attach the load and direct the operator, and the slings must be within thorough examination. Digging with a load chained to the bucket teeth is not lifting and is not acceptable.",
      },
    ],
  },

  "lift-dumper": {
    whoFor:
      "Written for site dumper operators and the supervisors who control muck-away and material movement, on sites where the principal contractor wants the overturn and tipping-edge risks addressed head on. Overturning is the leading cause of death involving site dumpers, usually on slopes, soft ground or at tipping edges, so a credible RAMS has to deal with seat belts, ground and edge protection. It sets out how the dumper is loaded, driven and tipped safely and what a reviewer checks before accepting it.",
    scope:
      "Covers the operation of a forward-tipping or swivel-tip site dumper moving spoil and materials around a construction site, including loading by excavator and tipping at a stockpile or into an excavation. It applies to travel on site haul routes, on gradients within the machine's limits, and to tipping at prepared, protected edges. It does not cover road travel, carrying passengers, or tipping at any edge that has not been assessed and provided with stop blocks. Loading the dumper by hand or operating it without ROPS and seat belt is outside scope.",
    legislation: [
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "The Construction (Design and Management) Regulations 2015",
      "HSG144 — The safe use of vehicles on construction sites",
      "Health and Safety at Work etc. Act 1974",
      "Workplace (Health, Safety and Welfare) Regulations 1992",
    ],
    sequence: [
      "Operator carries out pre-start checks: brakes, steering, tyres, ROPS, seat belt, skip pivot and the reversing alarm.",
      "Haul routes and gradients are confirmed against the machine's limits, with one-way running and segregated pedestrian routes where set out.",
      "The dumper is positioned for loading and held on the brake while the excavator loads the skip evenly within the rated payload.",
      "The operator drives on the assessed route at a controlled speed, seat belt fastened, descending slopes in gear and never overloading the skip.",
      "At the tipping point the dumper is reversed up to the stop blocks at the assessed edge, with a banksman where vision is limited.",
      "The skip is tipped while the machine is square to the edge and stationary, then lowered fully before the dumper moves off.",
    ],
    hazards: [
      {
        hazard: "Overturn on a slope, soft ground or at a tipping edge",
        who: ["Operator", "Site personnel"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Haul routes and gradients assessed against the machine's stated limits per HSG144",
          "Stop blocks/edge protection at every tipping point; dumper reversed square to the edge",
          "Skip loaded evenly within the rated payload and never tipped on a side slope",
          "ROPS fitted and seat belt worn so the operator is held within the protective structure",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Operator thrown from the machine in an overturn",
        who: ["Operator"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Seat belt worn at all times the machine is in use",
          "ROPS in place and not modified; cab/structure checked at pre-start",
          "Speed kept controlled so the machine stays within its stability envelope",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Pedestrian struck, particularly while reversing",
        who: ["Banksman", "Site personnel"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "One-way systems and segregated pedestrian routes to minimise reversing",
          "Banksman at tipping points and where vision is restricted; reversing alarm working",
          "Operator stops if anyone enters the working area until they are clear",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Loss of load or instability from overloading or uneven loading",
        who: ["Operator", "Excavator operator"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Skip loaded within the rated payload and the load spread evenly by the excavator",
          "No oversized or projecting material that could fall or obstruct the operator's view",
          "Load lowered fully and skip checked clear before travelling",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Run-away on a gradient",
        who: ["Operator", "Site personnel"],
        initial: { likelihood: 2, severity: 5 },
        controls: [
          "Parking brake applied and machine left in gear when stationary on any slope",
          "Slopes descended in gear under engine braking, not coasting or riding the brakes",
          "Gradients beyond the machine's limit avoided; alternative route used",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
    ],
    ppe: [
      "Hi-vis clothing",
      "Safety helmet",
      "Safety boots (steel toe)",
      "Gloves for pre-start checks and coupling",
    ],
    competence: [
      "CPCS/NPORS qualified forward-tipping/swivel-tip dumper operator",
      "Trained banksman for tipping and restricted-vision areas",
      "Site supervisor competent in HSG144 traffic and route control",
    ],
    permits: [
      "Authorisation to operate issued by the site manager for the named operator",
      "Permit/sign-off for any tipping point next to an excavation or open edge",
    ],
    equipment: [
      "Forward-tipping or swivel-tip site dumper with ROPS and seat belt",
      "Stop blocks/edge protection at tipping points",
      "Reversing alarm and flashing beacon",
      "Wheel-wash or route maintenance plant where haul routes degrade",
      "Banksman radios/whistle",
      "Route signage and pedestrian barriers",
    ],
    pcChecks: [
      "Stop blocks/edge protection at tipping points are specified and the dumper is required to reverse square to a protected edge — tipping into an excavation without that detail gets the RAMS returned.",
      "Seat belt use and a working ROPS are stated as mandatory, not left implicit.",
      "Haul-route gradients are tied to the machine's stated limits rather than 'drive carefully on slopes'.",
    ],
    faqs: [
      {
        q: "Why do dumpers need stop blocks at tipping edges?",
        a: "Most fatal dumper incidents are overturns, and tipping into an excavation or over an edge is a high-risk moment because the ground near the edge can be soft or give way, and the operator can reverse too far. A stop block — a substantial earth bund or timber baulk — gives the operator a positive limit to reverse up to so the wheels never reach the unstable edge. Combined with reversing square to the edge and a banksman, it is the recognised control for tipping points. HSG144 sets this out for vehicles on construction sites.",
      },
      {
        q: "Is a seat belt really necessary on a slow site dumper?",
        a: "Yes — it is one of the most important controls. In an overturn the danger is the operator being thrown out and crushed by the ROPS or the machine, and a seat belt keeps them inside the protective structure where they have the best chance of surviving. Dumpers overturn at low speed on slopes and soft ground, so the slow speed does not make it safe. The seat belt and ROPS work together, and the RAMS should treat both as mandatory whenever the machine is in use.",
      },
    ],
  },

  "lift-traffic-mgmt": {
    whoFor:
      "This document is for site managers and principal contractors setting up the traffic management arrangements that keep plant, vehicles and pedestrians apart across the whole site. It is not a single-machine method statement — it is the plan that the dumper, telehandler and delivery RAMS all sit underneath, and the PC is usually the one who has to own it. It sets out the routes, segregation, signage and delivery controls that a CDM client or auditor expects to see for a construction site traffic management plan.",
    scope:
      "Covers site-wide traffic management for a construction site: the layout of vehicle and pedestrian routes, segregation, signage, speed control, reversing arrangements and the management of deliveries through the site gate. It applies from the site entrance through the haul routes to loading, parking and tipping areas. It sets the framework that individual plant operations work within but does not replace the task-specific RAMS for each machine. Public-highway works and temporary traffic management on the adopted road network are covered by separate Chapter 8 arrangements and are outside this scope.",
    legislation: [
      "HSG144 — The safe use of vehicles on construction sites",
      "The Construction (Design and Management) Regulations 2015",
      "Workplace (Health, Safety and Welfare) Regulations 1992",
      "Health and Safety at Work etc. Act 1974",
      "The Management of Health and Safety at Work Regulations 1999",
    ],
    sequence: [
      "The site is surveyed and a traffic management plan drawn up showing vehicle routes, pedestrian routes, gates, parking, loading and tipping areas.",
      "Pedestrian routes are physically segregated from vehicle routes with barriers, and crossing points are kept to a minimum and clearly marked.",
      "One-way systems are established wherever possible so that reversing is designed out of the everyday movement of plant and vehicles.",
      "Signage, speed limits and lighting are installed at the gate and along routes, and the plan is briefed to everyone on site at induction.",
      "Deliveries are booked in and managed at the gate, with a banksman to receive and direct vehicles and to control reversing where it cannot be avoided.",
      "The plan is reviewed as the site develops, with routes and segregation updated as the works and the layout change.",
    ],
    hazards: [
      {
        hazard: "Pedestrian struck by a moving vehicle or item of plant",
        who: ["Site personnel", "Visitors", "Delivery drivers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Physically segregated pedestrian routes separated from vehicle routes by barriers",
          "Minimal, clearly marked and signed crossing points with priority given to pedestrians",
          "Speed limits, signage and lighting along all routes; plan briefed at induction",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Person struck during vehicle reversing",
        who: ["Site personnel", "Delivery drivers", "Banksman"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "One-way systems and turning areas designed to eliminate reversing wherever possible",
          "Trained banksman to control any reversing that cannot be designed out",
          "Reversing alarms and beacons required on all site vehicles by the plan",
        ],
        residual: { likelihood: 1, severity: 5 },
      },
      {
        hazard: "Collision between vehicles at junctions or in shared areas",
        who: ["Operators", "Delivery drivers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Defined routes with priority, signage and good visibility at junctions",
          "Speed limits set for the route conditions and enforced",
          "Loading and parking areas kept clear of through routes",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Uncontrolled vehicles entering at the site gate",
        who: ["Delivery drivers", "Site personnel", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Deliveries booked in and received at a managed gate with a banksman",
          "Drivers signed in, inducted to the route and not allowed to roam the site",
          "Gate kept clear of the public footway and pedestrians at the entrance protected",
        ],
        residual: { likelihood: 1, severity: 4 },
      },
      {
        hazard: "Routes degrading or becoming unsafe as the site changes",
        who: ["Site personnel", "Operators"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Traffic management plan reviewed regularly and as the layout changes",
          "Routes maintained, drained and kept free of obstructions and mud",
          "Changes re-briefed to the workforce and signage updated",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Hi-vis clothing for everyone on site",
      "Safety helmet",
      "Safety boots",
    ],
    competence: [
      "Site manager competent in HSG144 traffic management planning",
      "Trained banksmen/traffic marshals for gate and reversing control",
      "Inducted operators and drivers who understand the site plan",
    ],
    permits: [
      "Delivery booking/permit system controlling vehicle access through the gate",
      "Authorisation for any temporary change to routes or segregation",
    ],
    equipment: [
      "Traffic management plan drawing displayed at the gate",
      "Pedestrian barriers and route segregation",
      "Road signage, speed-limit and direction signs",
      "Stop/go boards or barriers at the gate",
      "Lighting for routes and crossings",
      "Banksman radios and hi-vis identifiers",
    ],
    pcChecks: [
      "The plan physically segregates pedestrians from vehicles with barriers and shows it on a drawing — a line saying 'pedestrians to take care around plant' is not a traffic management plan.",
      "Reversing is designed out through one-way systems, with banksman control named where it cannot be avoided.",
      "Delivery and gate management is addressed, including how drivers are received, inducted and directed.",
    ],
    faqs: [
      {
        q: "Who is responsible for the site traffic management plan?",
        a: "On a construction site the principal contractor is responsible for organising and controlling the site, and that includes the traffic management plan that keeps vehicles and pedestrians apart. Individual contractors must work within it and provide task-specific RAMS for their own plant, but the overarching plan is a PC duty under CDM 2015. The client and principal designer also have duties to ensure traffic risks are considered in the design and planning of the project. In practice the site manager draws up and maintains the plan and briefs it at induction.",
      },
      {
        q: "How do I reduce reversing on a tight site?",
        a: "The recognised approach in HSG144 is to design reversing out rather than rely on banksmen. One-way systems, turning circles and through-routes mean vehicles drive forward most of the time, and loading and tipping areas can be laid out so plant does not have to reverse to use them. Where some reversing is genuinely unavoidable, it should be controlled by a trained banksman in a safe position, with reversing alarms and good visibility. Designing it out first, then controlling what is left, is the order the plan should follow.",
      },
    ],
  },

  "lift-manual-handling": {
    whoFor:
      "This RAMS is for everyone on site who lifts, carries, pushes or pulls materials by hand — which is effectively the whole workforce — and for the supervisors who have to show the principal contractor that manual handling has been assessed. Unlike the plant RAMS it involves no machinery; the risk is musculoskeletal injury built up over many lifts, so the focus is on avoiding and reducing handling rather than guarding a machine. It applies the MHOR hierarchy and a TILE assessment to the handling tasks on your job.",
    scope:
      "Covers manual handling of materials, components and waste by hand on a construction site, including lifting, lowering, carrying, pushing and pulling. It applies wherever a handling task cannot be avoided or fully mechanised and a person has to move a load. It sets out the avoid–assess–reduce hierarchy required by the Manual Handling Operations Regulations and the use of mechanical aids and team lifts to cut the risk. It does not cover the operation of plant or lifting equipment, which are dealt with under their own RAMS, and it is not a substitute for task-specific assessment of unusually heavy or awkward loads.",
    legislation: [
      "Manual Handling Operations Regulations 1992 (MHOR)",
      "The Management of Health and Safety at Work Regulations 1999",
      "The Construction (Design and Management) Regulations 2015",
      "Health and Safety at Work etc. Act 1974",
      "INDG143 — Manual handling at work: a brief guide",
    ],
    sequence: [
      "Handling tasks are identified and, following the MHOR hierarchy, avoided or mechanised wherever reasonably practicable.",
      "Tasks that cannot be avoided are assessed using TILE — the Task, the Individual, the Load and the Environment.",
      "Mechanical aids such as trolleys, sack trucks, pallet trucks or wheelbarrows are provided to reduce the handling that remains.",
      "Loads are broken down, repackaged or delivered closer to the point of use to cut carrying distance and weight.",
      "Where a load still has to be lifted by hand, team lifts are used for heavy or awkward items with one person directing.",
      "Operatives are briefed on good lifting technique and on reporting any task that feels beyond a safe limit so it can be reassessed.",
    ],
    hazards: [
      {
        hazard: "Musculoskeletal injury from lifting loads that are too heavy",
        who: ["Operatives", "Labourers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Avoid or mechanise the lift where reasonably practicable per the MHOR hierarchy",
          "TILE assessment of any unavoidable lift; loads broken down to a manageable weight",
          "Mechanical aids provided; team lift used for heavy items with one person directing",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Back and shoulder strain from awkward postures and repetitive handling",
        who: ["Operatives", "Labourers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Storage and delivery arranged to avoid stooping, twisting and overhead lifting",
          "Task rotation and rest breaks to limit repetitive handling",
          "Operatives briefed on technique: stable base, bend the knees, keep the load close",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Trips and falls while carrying a load",
        who: ["Operatives", "Site personnel"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Carry routes kept clear, level, lit and free of trailing materials",
          "Load size limited so the operative can see over and around it",
          "Loads carried on trolleys where the route and floor allow",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
      {
        hazard: "Cuts, crush and puncture injuries to hands and feet from the load",
        who: ["Operatives", "Labourers"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "Cut-resistant gloves and safety boots worn for handling",
          "Sharp, banded or unstable loads handled with the right aid or de-banded safely",
          "Fingers and feet kept clear when setting loads down",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
      {
        hazard: "Injury from a team lift going wrong through poor coordination",
        who: ["Operatives", "Labourers"],
        initial: { likelihood: 3, severity: 3 },
        controls: [
          "One person nominated to direct the lift and give clear commands",
          "Team members matched for height/capability and the route planned and clear",
          "Load shared evenly and lifted, carried and set down together on command",
        ],
        residual: { likelihood: 1, severity: 2 },
      },
    ],
    ppe: [
      "Cut-resistant gloves",
      "Safety boots (steel toe)",
      "Hi-vis clothing",
      "Safety helmet where overhead risk exists",
    ],
    competence: [
      "Operatives given manual handling information, instruction and training",
      "Supervisor competent to carry out a TILE assessment",
      "Workforce briefed on the avoid–assess–reduce hierarchy for their tasks",
    ],
    permits: [
      "No permit required; unusually heavy or awkward loads referred for specific assessment before handling",
    ],
    equipment: [
      "Sack trucks and trolleys",
      "Pallet trucks for palletised loads",
      "Wheelbarrows for loose materials",
      "Lifting/carrying straps for awkward loads",
      "Mechanical aids (genie lift, material hoist) where provided",
    ],
    pcChecks: [
      "The RAMS shows the MHOR avoid–assess–reduce hierarchy was applied — handling that could have been mechanised but was not gets queried.",
      "A TILE assessment is referenced for the actual loads on the task, not a generic 'lift correctly' statement.",
      "Mechanical aids and team-lift arrangements are named for the heavy or awkward loads on the job.",
    ],
    faqs: [
      {
        q: "Is there a legal maximum weight one person can lift?",
        a: "No — MHOR does not set a fixed legal limit because the safe weight depends on the task, the individual and the conditions. The HSE provides guideline filter weights (for example around 25kg for a man lifting at knuckle height close to the body, less for women and for lifts away from the body), but these are screening figures, not limits. The duty is to avoid hazardous handling where reasonably practicable, assess what is left using TILE, and reduce the risk. If a load is near or above the guideline weights it should be assessed properly rather than just lifted.",
      },
      {
        q: "When should we use a team lift instead of one person?",
        a: "A team lift is appropriate when a load is too heavy or too awkward for one person to handle safely, but it does not simply double the safe weight — coordination problems mean the capability of a two-person team is less than the sum of the individuals. The lift should be planned, with one person directing, the team matched for height and capability, and the route clear before starting. Often a better answer is to break the load down or use a mechanical aid so a team lift is not needed at all. Team lifting is a control to reach for after avoiding and mechanising have been considered.",
      },
    ],
  },
};
